import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './components/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import Navbar from './components/Navbar';
import IncomingCallModal from './components/IncomingCallModal';
import ToastContainer from './components/Toast';
import { VoiceSessionProvider } from './components/VoiceSessionContext';
import FloatingVoiceBanner from './components/FloatingVoiceBanner';

// Eagerly load only the entry route — everything else is split.
import LandingPage from './pages/LandingPage';

// Route-level code splitting: each page becomes its own chunk.
const LoginPage              = lazy(() => import('./pages/LoginPage'));
const RegisterPage           = lazy(() => import('./pages/RegisterPage'));
const VerifyEmailPage        = lazy(() => import('./pages/VerifyEmailPage'));
const ForgotPasswordPage     = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage      = lazy(() => import('./pages/ResetPasswordPage'));
const CounselorsPage         = lazy(() => import('./pages/CounselorsPage'));
const CounselorApplyPage     = lazy(() => import('./pages/CounselorApplyPage'));
const CounselorDetailPage    = lazy(() => import('./pages/CounselorDetailPage'));
const ChatPage               = lazy(() => import('./pages/ChatPage'));
const ProfilePage            = lazy(() => import('./pages/ProfilePage'));
const AdminPage              = lazy(() => import('./pages/AdminPage'));
const TermsPage              = lazy(() => import('./pages/TermsPage'));
const PrivacyPage            = lazy(() => import('./pages/PrivacyPage'));
const SupportPage            = lazy(() => import('./pages/SupportPage'));
const NotificationsPage      = lazy(() => import('./pages/NotificationsPage'));
const DashboardPage          = lazy(() => import('./pages/DashboardPage'));
const BookingPage            = lazy(() => import('./pages/BookingPage'));
const OpenChatPage           = lazy(() => import('./pages/OpenChatPage'));
const OpenChatRoomPage       = lazy(() => import('./pages/OpenChatRoomPage'));
const RecordingsPage         = lazy(() => import('./pages/RecordingsPage'));
const InvitePage             = lazy(() => import('./pages/InvitePage'));
const BlogListPage           = lazy(() => import('./pages/BlogListPage'));
const BlogPostPage           = lazy(() => import('./pages/BlogPostPage'));
const BlogWritePage          = lazy(() => import('./pages/BlogWritePage'));
const BlogEditPage           = lazy(() => import('./pages/BlogEditPage'));
const RadioListPage          = lazy(() => import('./pages/RadioListPage'));
const RadioPlayerPage        = lazy(() => import('./pages/RadioPlayerPage'));
const RadioBroadcastPage     = lazy(() => import('./pages/RadioBroadcastPage'));
const RadioRecordingsPage    = lazy(() => import('./pages/RadioRecordingsPage'));
const VoiceRoomListPage      = lazy(() => import('./pages/VoiceRoomListPage'));
const VoiceRoomPage          = lazy(() => import('./pages/VoiceRoomPage'));
const ProductDetailPage      = lazy(() => import('./pages/ProductDetailPage'));

function RouteFallback() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center" aria-live="polite" aria-busy="true">
      <div className="w-8 h-8 border-2 border-luna-purple border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <AuthProvider>
      <VoiceSessionProvider>
        <Navbar />
        <IncomingCallModal />
        <ToastContainer />
        <FloatingVoiceBanner />
        <main className="pt-16">
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/counselors" element={<CounselorsPage />} />
              <Route path="/counselors/:id" element={<CounselorDetailPage />} />
              <Route path="/counselor-apply" element={<CounselorApplyPage />} />
              <Route path="/chat" element={<ChatPage />} />
              <Route path="/chat/:roomId" element={<ChatPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/booking/:counselorId" element={<BookingPage />} />
              <Route path="/open-chat" element={<OpenChatPage />} />
              <Route path="/open-chat/:roomId" element={<OpenChatRoomPage />} />
              <Route path="/open-chat/invite/:code" element={<InvitePage />} />
              <Route path="/recordings" element={<RecordingsPage />} />
              {/* Blog */}
              <Route path="/blog" element={<BlogListPage />} />
              <Route path="/blog/write" element={<BlogWritePage />} />
              <Route path="/blog/edit/:postId" element={<BlogEditPage />} />
              <Route path="/blog/:postId" element={<BlogPostPage />} />
              {/* Radio */}
              <Route path="/radio" element={<RadioListPage />} />
              <Route path="/radio/broadcast" element={<RadioBroadcastPage />} />
              <Route path="/radio/recordings" element={<RadioRecordingsPage />} />
              <Route path="/radio/:broadcastId" element={<RadioPlayerPage />} />
              {/* Shop - shareable product links */}
              <Route path="/shop/:productId" element={<ProductDetailPage />} />
              {/* Voice Rooms */}
              <Route path="/voice-rooms" element={<VoiceRoomListPage />} />
              <Route path="/voice-rooms/:roomId" element={<VoiceRoomPage />} />
            </Routes>
          </Suspense>
        </main>
      </VoiceSessionProvider>
      </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
